import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useDrawerFilter } from '@hapvida/hapvida-core-components';

import BeneficiaryService, {
  BeneficiariesSummaryProps,
  BeneficiariesProps,
} from '@services/BeneficiaryService';

import { toastifyApiErrors } from '@utils';
import { useFilterParams } from '@hooks';
import { useAuth } from 'hooks/useAuth';
import { ITEMS_LIMIT_PER_PAGE } from '@constants';
import { loadParamsBasedOnKeys } from 'hooks/services/beneficiaries/list/utils';

import { beneficiaryDrawerFilterValues } from '../../constants';

export function useBeneficiariesMobileState() {
  const { searchParam, mappedFilters } = useFilterParams();
  const { getInitialFilter, setActiveFilters } = useDrawerFilter();
  const { enqueueSnackbar } = useSnackbar();
  const initialRender = useRef(true);
  const { selectedCompany, selectedPartnerCompany } = useAuth();
  const [searchParams] = useSearchParams();

  const beneficiaryService = useMemo(() => new BeneficiaryService(), []);

  const companyId = selectedCompany?.id ?? '';
  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';

  const isEnabled = Boolean(companyId) && Boolean(partnerCompany);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      const initialFinancialFilters = getInitialFilter({
        filtersConfig: beneficiaryDrawerFilterValues,
      });

      setActiveFilters(initialFinancialFilters);
    }
  }, []);

  const mappedParams = loadParamsBasedOnKeys(searchParams, [
    'search',
    'beneficiaries-type',
    'reviews',
    'organizational-group',
    'start-date',
    'end-date',
  ]);

  const beneficiariesSummaryState = useQuery<BeneficiariesSummaryProps>({
    queryKey: [
      'beneficiariesSummaryState',
      mappedFilters,
      searchParam,
      companyId,
      partnerCompany,
      mappedParams,
    ],
    queryFn: () =>
      beneficiaryService.fetchBeneficiariesSummary({
        companyId,
        ...mappedFilters,
        ...searchParam,
        ...mappedParams,
      }),
    enabled: isEnabled,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const beneficiariesMobileState = useInfiniteQuery<BeneficiariesProps>({
    queryKey: [
      'beneficiariesState',
      mappedParams,
      searchParam,
      companyId,
      partnerCompany,
    ],
    queryFn: ({ pageParam = 1 }) =>
      beneficiaryService.fetchBeneficiaries({
        companyId,
        ...mappedParams,
        ...mappedFilters,
        ...searchParam,
        offset: ITEMS_LIMIT_PER_PAGE * (pageParam - 1),
        limit: ITEMS_LIMIT_PER_PAGE,
      }),
    enabled: isEnabled,
    getNextPageParam: (lastPage, allPages) => {
      const hasNextPage =
        lastPage.total > allPages.length * ITEMS_LIMIT_PER_PAGE;

      return hasNextPage ? allPages.length + 1 : undefined;
    },
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const refreshBeneficiariesState = () => {
    beneficiariesSummaryState.remove();
    beneficiariesMobileState.remove();

    beneficiariesSummaryState.refetch();
    beneficiariesMobileState.refetch();
  };

  return {
    beneficiariesMobileState,
    refreshBeneficiariesState,
    beneficiariesSummaryState,
  };
}
